import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CardContent,
  Skeleton,
} from "@mui/material";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ViewInArOutlined } from "@mui/icons-material";

// Calculation function
function calculateSustainabilityScore({
  currentMoisture,
  currentMoistureMin,
  currentTemp,
  currentWaterBalance,
  currentSalinity,
}) {
  // Handle null or undefined values
  const moisture = currentMoisture || 0;
  const moistureMin = currentMoistureMin || 0;
  const temp = currentTemp || 0;
  const waterBalance = currentWaterBalance || 0;
  const salinity = currentSalinity || 0;

  // 1. Soil Health Score
  let moistureImprovement = moistureMin > 0 ? moisture / moistureMin : 0;
  moistureImprovement = Math.min(moistureImprovement, 1); // Clamp to max 1
  const tempPenalty = Math.max(0, (temp - 30) * 2);
  const soilHealthScore = Math.max(0, (moistureImprovement * 100) - tempPenalty);

  // 2. Water Efficiency Score
  const waterEfficiencyScore = Math.min(waterBalance * 100 * 10, 100);

  // 3. Salinity Management Score
  let salinityScore = 20;
  if (salinity > 0.5) {
    salinityScore = Math.max(0, 20 - (salinity - 0.5) * 40);
  }

  // Final Score
  const score = Math.round(
    (soilHealthScore * 0.5) + (waterEfficiencyScore * 0.3) + (salinityScore * 0.2)
  );

  return score;
}

const SustainabilityScoreCard = ({ siteId }) => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [sustainabilityData, setSustainabilityData] = useState({
    score: null,
    changeFromLastMonth: null,
    lastMonthScore: null,
    loading: true,
    error: null,
  });

  const baseURL = process.env.REACT_APP_BASEURL;

  useEffect(() => {
    const fetchSustainabilityData = async () => {
      setSustainabilityData((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
      try {
        // Fetch devices (filter by siteId if provided)
        let deviceUrl = `${baseURL}/devices`;
        if (siteId && siteId !== "all") {
          deviceUrl += `/?site=${siteId}`;
        }
        const devicesRes = await axios.get(deviceUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const deviceIds = devicesRes.data.map((d) => d.id).join(",");

        if (!deviceIds) {
          setSustainabilityData({
            score: "N/A",
            changeFromLastMonth: "N/A",
            lastMonthScore: "N/A",
            loading: false,
          });
          return;
        }

        // Dynamic dates for 30-day window logic
        const currentMonthEnd = new Date();
        const startDate = new Date(currentMonthEnd);
        startDate.setDate(startDate.getDate() - 30);

        const endDate = currentMonthEnd.toISOString();

        // Last month end is the day before current month start
        const lastMonthEnd = new Date(startDate);
        lastMonthEnd.setDate(lastMonthEnd.getDate() - 1);

        // Last month start is 30 days before last month end
        const lastMonthStart = new Date(lastMonthEnd);
        lastMonthStart.setDate(lastMonthStart.getDate() - 30);

        // Fetch current month data
        const aggRes = await axios.get(`${baseURL}/measurements/aggregated`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            device: deviceIds,
            since: startDate,
            until: endDate,
            aggregate_all: true,
            fields: "moisture,temperature,water_balance,salinity",
          },
        });

        // Fetch last month data
        const lastMonthRes = await axios.get(`${baseURL}/measurements/aggregated`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            device: deviceIds,
            since: lastMonthStart,
            until: lastMonthEnd,
            aggregate_all: true,
            fields: "moisture,temperature,water_balance,salinity",
          },
        });

        // Extract metrics
        const currentMoisture = aggRes.data[0]?.moisture?.mean || 0;
        const currentMoistureMin = aggRes.data[0]?.moisture?.min || 0;
        const currentTemp = aggRes.data[0]?.temperature?.mean || 0;
        const currentWaterBalance = aggRes.data[0]?.water_balance?.mean || 0;
        const currentSalinity = aggRes.data[0]?.salinity?.mean || 0;

        const lastMonthMoisture = lastMonthRes.data[0]?.moisture?.mean || 0;
        const lastMonthMoistureMin = lastMonthRes.data[0]?.moisture?.min || 0;
        const lastMonthTemp = lastMonthRes.data[0]?.temperature?.mean || 0;
        const lastMonthWaterBalance = lastMonthRes.data[0]?.water_balance?.mean || 0;
        const lastMonthSalinity = lastMonthRes.data[0]?.salinity?.mean || 0;

        // Calculate current score
        const score = calculateSustainabilityScore({
          currentMoisture,
          currentMoistureMin,
          currentTemp,
          currentWaterBalance,
          currentSalinity,
        });

        // Calculate last month's score for comparison
        const lastMonthScore = calculateSustainabilityScore({
          currentMoisture: lastMonthMoisture,
          currentMoistureMin: lastMonthMoistureMin,
          currentTemp: lastMonthTemp,
          currentWaterBalance: lastMonthWaterBalance,
          currentSalinity: lastMonthSalinity,
        });

        // Calculate change
        const changeFromLastMonth = Math.round(score - lastMonthScore);

        setSustainabilityData({
          score: score || "N/A",
          changeFromLastMonth: changeFromLastMonth >= 0 ? `+${changeFromLastMonth}` : `${changeFromLastMonth}`,
          lastMonthScore: lastMonthScore || "N/A",
          loading: false,
        });
      } catch (error) {
        console.error(
          "Sustainability data fetch error:",
          error.response ? error.response.data : error.message
        );
        setSustainabilityData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load sustainability data",
        }));
      }
    };
    fetchSustainabilityData();
  }, [accessToken, siteId, baseURL]);

  const { score, changeFromLastMonth, lastMonthScore, loading, error } = sustainabilityData;

  const handleMoreClick = () => {
    navigate("/dashboard/statistics", {
      state: { siteId: siteId !== "all" ? siteId : null },
    });
  };

  if (loading)
    return (
      <Card
        sx={{
          width: "100%",
          borderRadius: 15,
          border: "2px solid black",
          backgroundColor: "#4CAF50",
          color: "#000",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: "rgba(203, 255, 227, 0.33)", mr: 1.5 }}
              />
              <Skeleton
                variant="text"
                width={150}
                height={40}
                sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
              />
            </Box>
            <Skeleton
              variant="rectangular"
              width={80}
              height={32}
              sx={{ borderRadius: 10, bgcolor: "rgba(0, 0, 0, 0.9)" }}
            />
          </Box>

          {/* Main Value */}
          <Skeleton
            variant="text"
            width={120}
            height={48}
            sx={{ my: 1, bgcolor: "rgba(255, 255, 255, 0.1)" }}
          />

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Skeleton
              variant="text"
              width={150}
              height={20}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
            />
            <Skeleton
              variant="text"
              width={60}
              height={20}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  if (error) return <Typography color="error">{error}</Typography>;

  const changeValue = parseFloat(changeFromLastMonth);
  const trendColor = changeValue > 0 ? "green" : changeValue < 0 ? "red" : "white";
  const TrendIcon = changeValue > 0 ? ArrowUpIcon : changeValue < 0 ? ArrowDownIcon : null;

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: 15,
        border: "2px solid black",
        backgroundColor: "#4CAF50",
        color: "#000",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mr: "1rem" }}>
            <ViewInArOutlined sx={{ fontSize: 40, mr: 1}} />
            <Typography variant="h4" component="div" fontWeight="bold" sx={{ lineHeight: "1" }}>
              Sustainability <br />Score
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#000000",
              borderRadius: 10,
              border: "2px solid white",
              px: 1.5,
              py: 0.5,
            }}
          >
            {TrendIcon && (
              <TrendIcon
                sx={{ fontSize: 16, mr: 0.5, color: trendColor }}
              />
            )}
            <Typography variant="body2" sx={{ fontWeight: 500, color: trendColor }}>
              {changeFromLastMonth}
            </Typography>
          </Box>
        </Box>

        <Box>

        </Box>
        <Typography variant="h3" fontWeight="bold" sx={{ my: 2 }}>
          {score !== null ? `${score}/100` : "N/A"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2">Last Month: {lastMonthScore}/100</Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={handleMoreClick}
          >
            <Typography variant="body2">More</Typography>
            <ChevronRightIcon fontSize="small" />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SustainabilityScoreCard;